import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    FormText
} from 'reactstrap';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import Header from '../../components/Headers/Header';
import { apiURL1 } from '../../config';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Footer from 'components/Footers/Footer';

const baseURL = apiURL1; // Replace with your actual base URL

function EditExam() {
    const token = localStorage.getItem("token");

    const { id } = useParams();
    const navigate = useNavigate();
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [course, setCourse] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [examType, setExamType] = useState('');
    const [examYear, setExamYear] = useState(null);
    const [examSchedule, setExamSchedule] = useState([]);
    const [loading, setLoading] = useState(false);
    const [examStatus, setExamStatus] = useState('draft'); // Default to draft
    const [isMid, setIsMid] = useState(false);

    useEffect(() => {
        fetchClasses();
        fetchExam();
    }, []);

    useEffect(() => {
        const fetchCourseAndSubjects = async () => {
            if (selectedClass) {
                try {
                    const classResponse = await axios.get(`${baseURL}/superadmin/oneClassForExam/${selectedClass}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`, // Include the token in the headers
                        }
                    });
                    setCourse(classResponse.data.data.course);

                    const courseResponse = await axios.get(`${baseURL}/superadmin/oneCourseForExam/${classResponse.data.data.course._id}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`, // Include the token in the headers
                        }
                    });
                    setSubjects(courseResponse.data.data.subjects);
                } catch (error) {
                    console.error('Error fetching course and subjects:', error);
                    toast.error('Error fetching course and subjects');
                }
            } else {
                setCourse(null);
                setSubjects([]);
                setExamSchedule([]);
            }
        };

        fetchCourseAndSubjects();
    }, [selectedClass]);

    const fetchClasses = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/allClassForExam`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            setClasses(response.data.data);
        } catch (error) {
            console.error('Error fetching classes:', error);
            toast.error('Error fetching classes');
        }
    };

    const fetchExam = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseURL}/superadmin/exams/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            const examData = response.data.data;
            setSelectedClass(examData.class._id);
            setCourse(examData.course);
            setExamType(examData.type);
            setExamYear(dayjs(examData.year.toString(), 'YYYY').toDate());
            setExamStatus(examData.status);
            setIsMid(Boolean(examData.isMid));
            setExamSchedule(examData.examDetails.map(detail => ({
                subjectId: detail.subject._id,
                teacherId: detail.teacher ? detail.teacher._id : null,
                examDate: dayjs(detail.examDate).format('YYYY-MM-DD'),
                startTime: detail.startTime,
                endTime: detail.endTime,
                subjectCode: detail.subject.subjectCode,
                subjectName: detail.subject.name,
                teacherName: detail.teacher ? detail.teacher.name : 'N/A'
            })));
        } catch (error) {
            console.error('Error fetching exam:', error);
            toast.error('Error fetching exam');
        } finally {
            setLoading(false);
        }
    };

    const handleTypeChange = (e) => {
        setExamType(e.target.value);
    };

    const handleYearChange = (date) => {
        setExamYear(date);
    };

    const handleScheduleChange = (index, field, value) => {
        const newSchedule = [...examSchedule];
        newSchedule[index][field] = value;
        setExamSchedule(newSchedule);
    };

    const handleStatusChange = (e) => {
        setExamStatus(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Validate that all fields are filled
            if (!selectedClass || !course || !examType || !examYear) {
                toast.error('Please fill in all the required fields.');
                setLoading(false);
                return;
            }

            // Validate exam schedule
            for (const item of examSchedule) {
                // console.log('item.examDate || !item.startTime || !item.endTime ', item.examDate, item.startTime, item.endTime)
                if (!item.examDate || !item.startTime || !item.endTime) {
                    toast.error('Please fill in all exam schedule details.');
                    setLoading(false);
                    return;
                }
            }

            const examDetails = examSchedule.map(item => ({
                subjectId: item.subjectId,
                teacherId: item.teacherId,
                examDate: item.examDate,
                startTime: item.startTime,
                endTime: item.endTime
            }));

            const payload = {
                classId: selectedClass,
                courseId: course._id,
                examSchedule: examDetails,
                type: examType,
                year: dayjs(examYear).year(),
                status: examStatus,
                isMid: isMid
            };

            await axios.put(`${baseURL}/superadmin/exams/${id}`, payload, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            toast.success('Exam updated successfully!');
            navigate('/admin/academic-progress-mangement');
        } catch (error) {
            console.error('Error updating exam:', error);
            toast.error('Error updating exam');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>Edit Exam</h3>
                                <Button color="primary" type="submit" disabled={loading} onClick={handleSubmit}>
                                    {loading ? <Spinner size="sm" /> : 'Update Exam'}
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <Form >
                                    <FormGroup>
                                        <Label>Class</Label>
                                        <Input
                                            type="select"
                                            value={selectedClass}
                                            disabled
                                        >
                                            {classes.map((cls) => (
                                                <option key={cls._id} value={cls._id}>
                                                    {cls.className}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>

                                    {course && (
                                        <FormGroup>
                                            <Label>Course</Label>
                                            <Input type="text" value={course.courseName} readOnly disabled />
                                        </FormGroup>
                                    )}

                                    <FormGroup>
                                        <Label>Exam Type</Label>
                                        <Input
                                            type="select"
                                            value={examType}
                                            onChange={handleTypeChange}
                                            required
                                            disabled
                                        >
                                            <option value="">Select Exam Type</option>
                                        </Input>
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Year</Label>
                                        <DatePicker
                                            selected={examYear}
                                            onChange={handleYearChange}
                                            showYearPicker
                                            dateFormat="yyyy"
                                            yearItemNumber={9}
                                            className="form-control"
                                            placeholderText="Select Year"
                                            disabled
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Exam Session</Label>
                                        <Input
                                            type="select"
                                            value={isMid ? 'true' : 'false'}
                                            onChange={(e) => setIsMid(e.target.value === 'true')}
                                            required
                                        >
                                            <option value="false">Semester</option>
                                            <option value="true">Mid Term</option>
                                        </Input>
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Status</Label>
                                        <div style={{ display: 'flex', gap: '20px' }}>
                                            <FormGroup check>
                                                <Label check>
                                                    <Input
                                                        type="radio"
                                                        name="status"
                                                        value="draft"
                                                        checked={examStatus === 'draft'}
                                                        onChange={handleStatusChange}
                                                    />
                                                    Draft
                                                </Label>
                                            </FormGroup>
                                            <FormGroup check>
                                                <Label check>
                                                    <Input
                                                        type="radio"
                                                        name="status"
                                                        value="approved"
                                                        checked={examStatus === 'approved'}
                                                        onChange={handleStatusChange}
                                                    />
                                                    Published
                                                </Label>
                                            </FormGroup>
                                        </div>
                                    </FormGroup>

                                    {subjects.length > 0 && (
                                        <>
                                            <h4>Exam Schedule</h4>
                                            {examSchedule.map((schedule, index) => (
                                                <Card key={schedule.subjectId} className="mb-3">
                                                    <CardBody>
                                                        <h5>{schedule.subjectName} ({schedule.subjectCode})</h5>
                                                        <p>Staff: {schedule.teacherName}</p>
                                                        <FormGroup>
                                                            <Label>Date</Label>
                                                            <Input
                                                                type="date"
                                                                value={schedule.examDate || ''}
                                                                onChange={(e) => handleScheduleChange(index, 'examDate', e.target.value)}
                                                                required
                                                            />
                                                        </FormGroup>
                                                        <FormGroup>
                                                            <Label>Start Time</Label>
                                                            <Input
                                                                type="time"
                                                                value={schedule.startTime || ''}
                                                                onChange={(e) => handleScheduleChange(index, 'startTime', e.target.value)}
                                                                required
                                                            />
                                                        </FormGroup>
                                                        <FormGroup>
                                                            <Label>End Time</Label>
                                                            <Input
                                                                type="time"
                                                                value={schedule.endTime || ''}
                                                                onChange={(e) => handleScheduleChange(index, 'endTime', e.target.value)}
                                                                required
                                                            />
                                                        </FormGroup>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </>
                                    )}
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Footer />
            </Container>
        </>
    );
}

export default EditExam;
